import { computed, getCurrentInstance, ref } from "vue";
import { useStore } from "@/core/services/store";
import { useRouter } from "vue-router/composables";
import { useAppLogo } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useAppLogo";
import { Error, Toast } from "@/core/plugins/swal";
import {
  addEventToLoadingQueue,
  removeEventFromLoadingQueue
} from "@/composables/useLoadingQueue";
import Project from "@/components/Settings/Projects/project";
import { UPDATE_SELECTED_PROJECT } from "@/core/services/store/switchArea.module";
import Presets from "@/components/Settings/Presets/presets";
import {
  loadForms,
  loadProjects,
  setSelectedApp,
  setXentralConfig
} from "@/composables/useSalesChannelManagementApp";
import ExternalConnection from "@/components/ExternalApps/SalesChannelManagementApp/classes/externalConnection";
import _ from "lodash";
import Assignments from "@/components/Assignments/assignments";
import { skipFields } from "@/components/Tools/FormHelper/Helper/constants";
import { useIntegration } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useIntegration";
import AuthValues from "@/components/Settings/Auth/authValues";
import KauflandSubscriptions from "@/components/ExternalApps/SalesChannelManagementApp/services/kauflandSubscriptions";
import { kauflandApp } from "@/components/ExternalApps/SalesChannelManagementApp/config/apps/kauflandApp";
import { amazonApp } from "@/components/ExternalApps/SalesChannelManagementApp/config/apps/amazonApp";
import { woltDeliveryApp } from "@/components/ExternalApps/SalesChannelManagementApp/config/apps/woltDeliveryApp";
import ProcessManager from "@/components/Workflows/processManager";
import { useUtility } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useUtility";
import { labelData } from "@/components/ExternalApps/SalesChannelManagementApp/utility/labelData";

export const useWizard = formComponent => {
  const i18n = getCurrentInstance().proxy.$i18n;
  const router = useRouter();
  const store = useStore();
  const { logo } = useAppLogo();
  const { getIntegrationsWithPresets } = useIntegration();
  const { xentralProjectHeader } = useUtility();

  const isLocal = store.getters.isLocal;

  const currentStep = ref(0);
  const isSaving = ref(false);
  const requests = ref([]);
  const error = ref(false);

  const validCredentials = ref(false);
  const validSftp = ref(false);
  const validName = ref(true);
  const skippedSteps = ref([]);
  const saveFormatters = ref({ saveAssignmentTable });
  const assignmentTableIds = ref([]);
  const baseDataNameBlacklist = ref([]);

  const integration = ref({
    baseData: {},
    credentials: {},
    features: {}
  });
  const defaultValues = ref({});

  const wizardSteps = computed(() => {
    const steps = Object.keys(store.getters.form("wizard") ?? {});

    if (
      store.getters.selectedApp?.name === woltDeliveryApp.name &&
      !steps.includes("credentials")
    ) {
      const baseDataIndex = steps.indexOf("baseData");
      steps.splice(baseDataIndex + 1, 0, "credentials");
    }

    return steps;
  });

  const step = computed(() => {
    return wizardSteps.value[currentStep.value];
  });

  const form = computed(() => {
    return store.getters.form("wizard." + step.value);
  });

  const formConfig = computed(() => {
    return {
      heading: step.value
        ? i18n.t("salesChannelManagementApp.titles." + step.value)
        : "",
      description: step.value
        ? i18n.t("salesChannelManagementApp.descriptions." + step.value)
        : "",
      snippetPrefix: "salesChannelManagementApp",
      labelStacked: step.value !== "features",
      labelColClass: "col-7",
      fieldColClass: "col-5",
      fieldColClassXl: "col-5",
      ...labelData.value
    };
  });

  // Local dev only: the credentials and sftpData steps are the ones gated on a
  // reachable third party, so those are the only steps worth skipping.
  const canSkipValidation = computed(
    () => isLocal && ["credentials", "sftpData"].includes(step.value)
  );

  const nextButtonDisabled = computed(() => {
    if (store.getters.appConfig.showTestConnection === false) {
      validCredentials.value = true;
      return false;
    }

    const currentStepKey = wizardSteps.value[currentStep.value];
    const features = integration.value?.features;

    const isFeatureStepInvalid =
      currentStepKey === "features" &&
      (!features || !Object.values(features).some(feature => feature));

    const isBaseDataStepInvalid =
      currentStepKey === "baseData" && !validName.value;

    const isCredentialsStepInvalid =
      currentStepKey === "credentials" && !validCredentials.value;

    const isSftpDataStepInvalid =
      currentStepKey === "sftpData" && !validSftp.value;

    const isLoading =
      store.getters["loadingQueue/showLoadingBar"]("saveWizard") ||
      store.getters["loadingQueue/showLoadingBar"]("validate");

    return (
      isFeatureStepInvalid ||
      isBaseDataStepInvalid ||
      isCredentialsStepInvalid ||
      isSftpDataStepInvalid ||
      isLoading
    );
  });

  function onChange(field) {
    // Editing a credential field invalidates a previously passed connection test. Skip the reset for
    // apps with showTestConnection: false (e.g. websales, temu): nothing ever re-validates in that case
    // (nextButtonDisabled's showTestConnection branch returns before touching validCredentials, so it
    // never runs again once a dependency it doesn't track changes), which permanently blocked the wizard
    // on any app whose credentials happen to include a field literally named "url" -- reported for
    // websales in CONNECT-6930, but latent for every showTestConnection:false app.
    const testConnectionFields = store.getters.appConfig
      .testConnectionFields ?? ["url", "token", "key", "secret"];

    if (
      store.getters.appConfig.showTestConnection !== false &&
      testConnectionFields.includes(field.name)
    ) {
      validCredentials.value = false;
      return;
    }

    if (field.name === "label") {
      validName.value =
        !baseDataNameBlacklist.value?.includes(field.value?.trim()) ?? true;
    }
  }

  async function setBaseDataNameBlacklist() {
    addEventToLoadingQueue({ key: "getPresetConfigs" });

    const presetConfigFilter = [
      {
        key: "name",
        op: "equals",
        value: "baseData"
      }
    ];
    const presetConfigs = await Presets.getAll(
      {
        showAll: true,
        noPagination: true
      },
      presetConfigFilter
    );
    const baseDataNameArray = presetConfigs.data
      .map(preset => preset.value?.name)
      .filter(Boolean);
    baseDataNameBlacklist.value = [...new Set(baseDataNameArray)];

    removeEventFromLoadingQueue({ key: "getPresetConfigs" });
  }

  function setIntegrationDefaultValues(ignoreWizard = false) {
    const form = store.getters.form();

    for (const [key, areas] of Object.entries(form)) {
      if (ignoreWizard && key === "wizard") {
        continue;
      }

      if (key === "settings" || key === "migration") {
        defaultValues.value[key] = processAreas(areas);
      } else {
        defaultValues.value = {
          ...defaultValues.value,
          ...processAreas(areas)
        };
      }
    }
  }

  //Loads the old installation progress when loading via the config parameter
  async function getPreviousConfig() {
    const xentralConfigData = store.getters.xentralConfig?.data ?? [];

    if (xentralConfigData["state"] && xentralConfigData["state"].length) {
      const decodedParams = base64ToUtf8(xentralConfigData["state"]);

      // The redirect state is normally the integration object (hosted, after the
      // token exchange adds credentials.success). Locally the dispatcher forwards
      // the raw provider callback, so we receive the full OAuth state with the
      // integration nested under `integration` and no success flag (CONNECT-6119).
      const isFullState = decodedParams.integration !== undefined;
      const integrationData = isFullState
        ? decodedParams.integration
        : decodedParams;

      // Locally there is no token exchange to add the success flag, so mark the
      // credentials as already authorized — this mirrors the hosted post-exchange
      // state so saveData skips re-storing the OAuth credentials (storeAuthValue
      // returns null when value.success is set), matching the normal flow.
      if (isFullState) {
        integrationData.credentials = {
          ...integrationData.credentials,
          success: true
        };
      }
      let appName =
        decodedParams.selectedAppName ?? integrationData.selectedAppName;

      await setSelectedApp(appName);
      await loadForms(appName, false);
      setIntegrationDefaultValues(true);
      integration.value = integrationData;
      currentStep.value = 2;
      validCredentials.value = integrationData.credentials?.success ?? isFullState;

      await Toast.fire({
        icon: "success",
        title: i18n.t(`salesChannelManagementApp.${appName}.authSuccess`)
      });
    }
  }

  const base64ToUtf8 = base64UrlEncoded => {
    // decode  url encoded characters
    const base64 = decodeURIComponent(base64UrlEncoded);

    // decode the base64 string
    const binaryString = atob(base64);
    const bytes = Uint8Array.from(binaryString, char => char.charCodeAt(0));
    const json = new TextDecoder().decode(bytes);

    return JSON.parse(json);
  };

  /**
   * @param {object} area
   * @returns {*}
   */
  function processFieldArray(area) {
    return area.reduce((acc, field) => {
      if (skipFields.includes(field.type)) return acc;
      acc[field.name] = field.default ?? null;
      return acc;
    }, {});
  }

  function processFieldsObject(area) {
    return Object.entries(area).reduce((acc, [fieldsKey, fields]) => {
      acc[fieldsKey] = {
        ...acc[fieldsKey],
        ...processFieldArray(fields)
      };
      return acc;
    }, {});
  }

  function processAreas(areas) {
    return Object.entries(areas).reduce((acc, [areaKey, area]) => {
      acc[areaKey] = {};

      if (Array.isArray(area)) {
        acc[areaKey] = {
          ...acc[areaKey],
          ...processFieldArray(area)
        };
      } else {
        acc[areaKey] = processFieldsObject(area);
      }

      return acc;
    }, {});
  }

  function next() {
    const currentStepKey = wizardSteps.value[currentStep.value];
    const isValidForm =
      isStepSkipped(currentStepKey) || formComponent.value?.validate() === true;

    if (!isValidForm) {
      return;
    }

    if (currentStepKey === "features") {
      const features = integration.value?.features;

      if (!features || !Object.values(features).some(feature => feature)) {
        Toast.fire({
          icon: "warning",
          title: i18n.t("salesChannelManagementApp.featureRequired")
        });
        return;
      }
    }
    if (currentStepKey === "credentials") {
      if (!validCredentials.value) {
        Toast.fire({
          icon: "warning",
          title: i18n.t("salesChannelManagementApp.validateCredentialsError")
        });

        return;
      }
    }

    if (currentStep.value < wizardSteps.value.length - 1) {
      currentStep.value++;
    } else if (currentStep.value === wizardSteps.value.length - 1) {
      saveData();
    }
  }

  function isStepSkipped(stepKey) {
    return isLocal && skippedSteps.value.includes(stepKey);
  }

  /**
   * Local dev only: create integrations without valid third-party credentials.
   * Marks the current step as skipped, which lifts both the connection check
   * and the FormHelper required-field check in next().
   * Never reachable in a hosted build - isLocal is a build-time VUE_APP_* flag.
   */
  function skipValidation() {
    if (!isLocal) return;

    validCredentials.value = true;
    validSftp.value = true;

    if (!skippedSteps.value.includes(step.value)) {
      skippedSteps.value = [...skippedSteps.value, step.value];
    }
  }

  function validateCredentials() {
    let selectedAppParams = JSON.parse(
      JSON.stringify(store.getters.appConfig?.additional_params ?? {})
    );

    const credentials = Object.entries(integration.value.credentials).length
      ? integration.value.credentials
      : formComponent.value.credentials.value;

    const params = {
      credentials,
      ...selectedAppParams
    };

    if (selectedAppParams.authType === "skip") {
      validCredentials.value = true;
      return;
    }

    addEventToLoadingQueue({ key: "validateCredentials", group: "validate" });

    ExternalConnection.checkApi(params)
      .then(async response => {
        if (!response.success) {
          // A backend that knows why the check failed says so (e.g. an expired Temu token).
          Toast.fire({
            icon: "warning",
            title:
              response.message ??
              i18n.t("salesChannelManagementApp.validateCredentialsError")
          });
          return;
        }

        if (selectedAppParams.authType === "oauth1") {
          integration.value.credentials.requestMethod =
            response?.credentials?.requestMethod ?? "header";
        }

        await loadForms(
          {
            sections: ["app", "migration"],
            credentials: integration.value.credentials
          },
          false
        ).then(() => {
          setIntegrationDefaultValues();
          validCredentials.value = true;

          removeEventFromLoadingQueue({
            group: "validateCredentials",
            type: "success",
            prefix: "salesChannelManagementApp",
            name: "validateCredentialsSuccess"
          });
        });
      })
      .finally(() => {
        removeEventFromLoadingQueue({ key: "validateCredentials" });
      });
  }

  function validateSftpData() {
    let params = {
      sftpData: integration.value.sftpData
    };

    addEventToLoadingQueue({ key: "validateSftpData", group: "validate" });

    ExternalConnection.checkSftp(params)
      .then(async response => {
        if (!response.success) {
          await Toast.fire({
            icon: "warning",
            title: i18n.t("salesChannelManagementApp.validateCredentialsError")
          });

          validSftp.value = false;

          return;
        }

        validSftp.value = true;

        removeEventFromLoadingQueue({
          group: "validateSftpData",
          type: "success",
          prefix: "salesChannelManagementApp",
          name: "validateCredentialsSuccess"
        });
      })
      .finally(() => {
        removeEventFromLoadingQueue({ key: "validateSftpData" });
      });
  }

  function back() {
    if (currentStep.value > 0) {
      currentStep.value--;
    }
  }

  async function saveFormatter(
    saveFormatter,
    field,
    value,
    createdIntegration,
    fieldPath = ""
  ) {
    return await saveFormatters.value[saveFormatter](
      field,
      value,
      createdIntegration,
      fieldPath
    );
  }

  async function saveAssignmentTable(
    field,
    value,
    createdIntegration,
    fieldPath
  ) {
    let returnValue = value;
    const assignmentTableName = `${createdIntegration.name}.${fieldPath}.${field.name}`;

    await Assignments.store({
      name: assignmentTableName,
      values: value,
      projects: [createdIntegration.id]
    }).then(response => {
      returnValue = response.data?.id;
      assignmentTableIds.value.push(returnValue);
    });

    return returnValue;
  }

  function resetXentralConfig() {
    //Remove state property from xentralConfig vuex store
    let xentralData = store.getters.xentralConfig;

    // Create a new object excluding the 'state' property
    let newData = { ...xentralData.data };
    delete newData.state;

    let updatedXentralConfig = { ...xentralData, data: newData };

    setXentralConfig(updatedXentralConfig);
  }

  async function saveData() {
    isSaving.value = true;

    // baseData.name is derived from the user-entered baseData.label. On this
    // first save the two must be identical, so mirror it here (single source of
    // truth) instead of relying on a reactive watcher that may not have fired.
    // Keeps channelSpecificName from being empty or stale. label is required.
    integration.value.baseData.name = integration.value.baseData.label.trim();

    const projectName =
      store.getters.selectedApp.name + " " + integration.value.baseData.name;

    addEventToLoadingQueue({ key: "wizardSaveProject", group: "saveWizard" });

    resetXentralConfig();

    // Reset values
    requests.value = [];
    error.value = false;
    assignmentTableIds.value = [];

    let projectId;
    let errorMessage;

    Project.store({
      name: projectName,
      preset_id: store.getters.presetId
    })
      .then(async response => {
        let project = response.data;
        projectId = project?.id;

        if (project.error) {
          errorMessage = project?.error;
          error.value = true;
          isSaving.value = false;

          return;
        }

        await store.dispatch(UPDATE_SELECTED_PROJECT, project);

        storePresetValue("channelIdentifier", projectId, projectId, "text");
        storePresetValue(
          "channel",
          projectId,
          store.getters.selectedApp.name,
          "text"
        );

        for (const area of wizardSteps.value) {
          addEventToLoadingQueue({ key: area, group: "saveWizard" });
          if (area === "credentials") {
            const authValuePromise = storeAuthValue(
              projectId,
              integration.value[area]
            );
            if (
              authValuePromise &&
              store.getters.selectedApp.name === kauflandApp.name
            ) {
              const kauflandSubscriptionPromise = authValuePromise
                .then(() => KauflandSubscriptions.create())
                .catch(() => (error.value = true));
              requests.value.push(kauflandSubscriptionPromise);
            }
          }
          storePresetValue(area, projectId, integration.value[area]);

          if (error.value) return;
        }

        if (store.getters.selectedApp.name === amazonApp.name) {
          storePresetValue("tmp", projectId, {
            salesOrderSyncToXentralActive: true
          });
        }

        for (const settingKey of Object.keys(
          defaultValues.value?.settings ?? {}
        )) {
          // Settings presets are stored under the plain area key (e.g. `taxes`),
          // matching how they are loaded again in setIntegrationData
          storePresetValue(
            settingKey,
            projectId,
            defaultValues.value.settings[settingKey]
          );
        }

        storePresetValue("migration", projectId, defaultValues.value.migration);

        for (const featureKey of Object.keys(
          integration.value?.features ?? {}
        )) {
          const featureValue = integration.value.features[featureKey];
          if (!featureValue) {
            continue;
          }

          const areas = store.getters.form("app." + featureKey);
          for (const areaKey of Object.keys(areas || {})) {
            const area = areas[areaKey];
            const name = featureKey + _.upperFirst(areaKey);

            for (const field of Object.values(area)) {
              if (field.saveFormatter) {
                const fieldPath = `${featureKey}.${areaKey}`;

                await saveFormatter(
                  field.saveFormatter,
                  field,
                  defaultValues.value[featureKey][areaKey][field.name] ?? [],
                  store.getters.selectedProject,
                  fieldPath
                ).then(response => {
                  defaultValues.value[featureKey][areaKey][field.name] =
                    response;
                });
              }
            }

            storePresetValue(
              name,
              projectId,
              defaultValues.value[featureKey][areaKey]
            );

            if (error.value) return;
          }
        }

        await Promise.all(requests.value);
      })
      .catch(() => (error.value = true))
      .finally(async () => {
        const integrationName = projectName
          .replace(store.getters.selectedApp.name, "")
          .trim();
        const notification = {
          type: "success",
          snippet: "wizardSuccess"
        };
        setIntegrationDefaultValues();

        // If an error occurred during saving, we delete every created config value and the created project
        // Then we switch back to the first step
        if (error.value) {
          await rollbackProject(projectId, errorMessage);

          removeEventFromLoadingQueue({
            group: "saveWizard"
          });

          isSaving.value = false;
          return;
        }

        const createsSalesXentralSalesChannel =
          !store.getters.selectedApp?.skipSalesChannelCreation ?? true;

        if (createsSalesXentralSalesChannel) {
          try {
            addEventToLoadingQueue({
              key: "wizardCreateSalesChannel",
              group: "saveWizard"
            });

            await ProcessManager.run(
              store.getters.createSalesChannelWorkflowId,
              {
                params: {
                  channelSpecificName: integrationName,
                  projectId: projectId,
                  appName: store.getters.selectedApp.name
                }
              },
              xentralProjectHeader.value
            );
          } catch (e) {
            notification.type = "warning";
            notification.snippet = "wizardSalesChannelNotCreated";
          }
        }

        await store.commit("loadSelectedIntegration", {});
        await loadProjects();
        await getIntegrationsWithPresets();

        removeEventFromLoadingQueue({
          group: "saveWizard",
          type: notification.type,
          prefix: "salesChannelManagementApp",
          name: notification.snippet,
          data: {
            integrationName
          }
        });

        isSaving.value = false;

        close();
      });
  }

  async function rollbackProject(projectId, errorMessage = null) {
    Error(errorMessage ?? i18n.t("salesChannelManagementApp.wizardError"));

    // If project was successfully created - delete it
    if (projectId) {
      await Project.destroyIntegration(projectId).then(() => {
        removeEventFromLoadingQueue({
          group: "saveWizard"
        });
        const name = integration.value?.baseData?.label;
        Object.assign(integration.value, {
          baseData: { label: name, name: name, active: false }
        });
        currentStep.value = 0;
      });
    }
  }

  /**
   *
   * @param name
   * @param createdProject
   * @param value
   * @param type
   */
  function storePresetValue(name, createdProject, value = [], type = "json") {
    const preset = Presets.store(
      {
        name: name,
        label: name,
        type: type,
        value: value,
        project_id: createdProject
      },
      true
    ).catch(() => (error.value = true));
    requests.value.push(preset);
  }

  function storeAuthValue(createdProject, value = []) {
    if (value?.success) {
      return null;
    }
    const authValues = AuthValues.store({
      name: "credentials",
      label: "Credentials",
      value: value,
      project_id: createdProject
    }).catch(() => (error.value = true));
    requests.value.push(authValues);
    return authValues;
  }

  function close() {
    resetXentralConfig();
    router.push({
      name: "salesChannelManagementApp",
      params: {
        name: store.getters.selectedApp.name
      }
    });
  }

  return {
    canSkipValidation,
    currentStep,
    form,
    formConfig,
    integration,
    isLocal,
    isSaving,
    logo,
    step,
    wizardSteps,
    nextButtonDisabled,
    validName,
    setBaseDataNameBlacklist,
    back,
    close,
    next,
    skipValidation,
    validateCredentials,
    validateSftpData,
    onChange,
    setIntegrationDefaultValues,
    getPreviousConfig
  };
};
